import {Card} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import i18n from '@dms/i18n';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';

import type {ArticleStockAgingBucket} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER} from 'shared/web';

const BUCKET_APPEARANCE = {
  months0to3: {range: '0–3', color: 'palettes.green.60.100'},
  months3to6: {range: '3–6', color: 'palettes.green.40.100'},
  months6to12: {range: '6–12', color: 'palettes.orange.60.100'},
  months12to24: {range: '12–24', color: 'palettes.red.50.100'},
  months24plus: {range: '24+', color: 'palettes.red.70.100'},
} as const;

interface StatisticsAgingProps {
  buckets: ArticleStockAgingBucket[];
  currency: string;
  handlingUnitLabel: string;
}

export function StatisticsAging(props: StatisticsAgingProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const isComplete = props.buckets.every((bucket) => bucket.isComplete);
  const totalQuantity = props.buckets.reduce((sum, bucket) => sum + (bucket.quantity ?? 0), 0);
  const totalValue = props.buckets.reduce((sum, bucket) => sum + (bucket.purchaseValue ?? 0), 0);
  const monthsLabel = i18n.t('general.labels.months');

  return (
    <Card title={i18n.t('entity.warehouse.reporting.valueByStockAge')}>
      {isComplete ? (
        <VStack spacing={3}>
          <Text color="secondary">{i18n.t('general.labels.today')}</Text>
          <HStack justify="space-between" align="center" wrap>
            <Text color="secondary">
              {getQuantityWithHandlingUnit(totalQuantity, props.handlingUnitLabel)}
            </Text>
            <Text>{formatCurrency(totalValue, props.currency, 2)}</Text>
          </HStack>
          <HStack spacing={0} width="100%">
            {props.buckets
              .filter((bucket) => (bucket.sharePercent ?? 0) > 0)
              .map((bucket) => (
                <Box
                  key={bucket.bucket}
                  width={`${bucket.sharePercent ?? 0}%`}
                  height={4}
                  backgroundColor={BUCKET_APPEARANCE[bucket.bucket].color}
                />
              ))}
          </HStack>
          <VStack spacing={2}>
            {props.buckets.map((bucket) => (
              <HStack key={bucket.bucket} justify="space-between" align="center" wrap>
                <HStack spacing={2} align="center">
                  <Box
                    width={3}
                    height={3}
                    backgroundColor={BUCKET_APPEARANCE[bucket.bucket].color}
                  />
                  <Text>{BUCKET_APPEARANCE[bucket.bucket].range + ' ' + monthsLabel}</Text>
                </HStack>
                <HStack spacing={3} align="center">
                  <Text>
                    {bucket.quantity === null
                      ? EMPTY_PLACEHOLDER
                      : getQuantityWithHandlingUnit(bucket.quantity, props.handlingUnitLabel)}
                  </Text>
                  <Text>
                    {bucket.purchaseValue === null
                      ? EMPTY_PLACEHOLDER
                      : formatCurrency(bucket.purchaseValue, props.currency, 2)}
                  </Text>
                  <Text color="secondary">
                    {bucket.sharePercent === null
                      ? EMPTY_PLACEHOLDER
                      : (formatNumber(bucket.sharePercent, 1) ?? EMPTY_PLACEHOLDER) + ' %'}
                  </Text>
                </HStack>
              </HStack>
            ))}
          </VStack>
        </VStack>
      ) : (
        <Text>{i18n.t('entity.warehouse.reporting.unknownDataNote')}</Text>
      )}
    </Card>
  );
}
